import { onAuthStateChanged } from "firebase/auth";
import { auth } from "./firebase";

import { db } from "./firebase";

import {
  collection,
  addDoc,
  serverTimestamp,
  query,
  orderBy,
  limit,
  getDocs,
  getDoc,
  deleteDoc,
  doc,
  setDoc,
} from "firebase/firestore";

import { useEffect, useState, useRef } from "react";
import Profile from "./pages/Profile/Profile";
import History from "./pages/History/History";
import Analysis from "./pages/Analysis/Analysis";
import Settings from "./pages/Settings/Settings";
import Reset from "./pages/Reset/Reset";
import Login from "./pages/Login/Login";
import Register from "./pages/Login/Register";
import Guides from "./pages/Guides/Guides";
import PrivacyPolicy from "./PrivacyPolicy";
import TermsConditions from "./TermsConditions";
import RefundPolicy from "./RefundPolicy";
import {
  exportFilteredHistory,
  resetAnalysisOnly,
} from "./helpers/resetHelpers";
import Onboarding from "./onboarding";
import Contact from "./pages/Contact";
import HelpFeedback from "./help-feedback-popover";
import Sidebar from "./components/Sidebar";
import TopBar from "./components/TopBar";
import { getProfilePhoto, saveProfilePhoto } from "./utils/indexedDB";
import { Analytics } from "@vercel/analytics/react";

/* 🔹 Sidebar scrollbar CSS (GLOBAL) */
const sidebarScrollbarCSS = `
  /* Chrome / Edge / Safari */
  ::-webkit-scrollbar {
    width: 6px;
  }

  ::-webkit-scrollbar-track {
    background: transparent;
  }

  ::-webkit-scrollbar-thumb {
    background: rgba(124, 58, 237, 0.35);
    border-radius: 999px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: rgba(124, 58, 237, 0.6);
  }
`;

const Chart = window.Chart;

// ⏱ Expected average time per question (c)
const EXPECTED_TIME = 2; // seconds (change anytime)

/* ================= THEME SYSTEM ================= */

/* ================= MASTER SIDEBAR THEME ================= */

const SIDEBAR_THEME = {
  dark: {
    bgMain: "#020617",
    bgGradient: "radial-gradient(1200px 600px at top, #1c2a44, #020617)",
    card: "#0b1220",
    border: "rgba(148, 163, 184, 0.12)",
    text: "#e5e7eb",
    muted: "#94a3b8",
    accent: "#7c3aed",
    accentGradient: "linear-gradient(135deg, #7c3aed, #9333ea)",
    danger: "#ef4444",
  },

  light: {
    bgMain: "#f8fafc",
    bgGradient: "linear-gradient(180deg, #f8fafc, #eef2ff)",
    card: "#ffffff",
    border: "rgba(15, 23, 42, 0.12)",
    text: "#0f172a",
    muted: "#475569",
    accent: "#7c3aed",
    accentGradient: "linear-gradient(135deg, #7c3aed, #9333ea)",
    danger: "#dc2626",
  },
};

/* ================= APP ================= */

/* ================= PRACTICE ================= */
function Practice({ settings, calcIframeRef }) {
  return (
    <div style={{ height: "calc(100vh - 60px)" }}>
      <iframe
        ref={calcIframeRef}
        src="/calculator.html"
        style={{
          width: "100%",
          height: "100%",
          border: "none",
          borderRadius: 12,
        }}
        onLoad={(e) => {
          const iframe = e.target;

          const payload = {
            type: "SETTINGS_UPDATE",
            theme:
              settings.theme === "system"
                ? window.matchMedia("(prefers-color-scheme: dark)").matches
                  ? "dark"
                  : "light"
                : settings.theme,
            showTimer: settings.showTimer,
            showAbort: settings.showAbort,
            direction: settings.direction,
            timerMode: settings.timerMode,
            showNumber: settings.showNumber,
            showKeypad: settings.showKeypad,
            streamSpeed: settings.streamSpeed,
          };

          // 🔥 send immediately
          iframe.contentWindow.postMessage(payload, "*");

          // 🔥 send again after iframe JS is ready
          setTimeout(() => {
            iframe.contentWindow.postMessage(payload, "*");
          }, 100);
        }}
      />
    </div>
  );
}

export default function App() {
  // 🔐 AUTH STATE
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  // 👤 USER PROFILE (GLOBAL)
  const [userProfile, setUserProfile] = useState(null);

  // 📌 UI STATE
  const [open, setOpen] = useState(false);
  const [section, setSection] = useState("practice");

  // 🔐 sections that must NOT show sidebar/topbar
  const AUTH_ONLY_SECTIONS = [
    "login",
    "register",
    "terms-auth",
    "privacy-auth",
  ];
  const [hoveredMenu, setHoveredMenu] = useState(null);

  // 🎨 THEME
  const [sidebarTheme, setSidebarTheme] = useState(SIDEBAR_THEME.dark);
  const [forceLightAuth, setForceLightAuth] = useState(false);

  const [showOnboarding, setShowOnboarding] = useState(false);
  const calcIframeRef = useRef(null);

  const [testHistory, setTestHistory] = useState([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (user) {
        await user.reload();

        if (user.emailVerified) {
          setIsAuthenticated(true);

          const uid = user.uid;

          // ✅ 1. ENSURE PROFILE DOCUMENT EXISTS
          const profileRef = doc(db, "users", uid, "profile", "main");
          const snap = await getDoc(profileRef);

          if (!snap.exists()) {
            await setDoc(profileRef, {
              Name: "",
              Class: "",
              Gender: "",
              DOB: "",
              Age: "",
              Email: user.email || "",
              createdAt: serverTimestamp(),
            });
          }

          // ✅ 2. MIGRATE ONBOARDING (GUEST → AUTH USER)
          const localProfile = localStorage.getItem("profileData");

          if (localProfile) {
            const parsed = JSON.parse(localProfile);
            // 🔒 CRITICAL: NEVER send photo to Firestore
            delete parsed.photo;
            delete parsed.Photo;
            await setDoc(
              profileRef,
              {
                ...parsed,
                Email: user.email, // 🔒 AUTH EMAIL ALWAYS WINS
                updatedAt: serverTimestamp(),
                migratedFromGuest: true,
              },
              { merge: true }
            );

            // 🔥 REMOVE GUEST DATA AFTER MIGRATION
            localStorage.removeItem("profileData");
            // 🔄 MIGRATE PROFILE PHOTO (IndexedDB → AUTH SESSION)
            const guestPhoto = await getProfilePhoto();
            if (guestPhoto) {
              await saveProfilePhoto(guestPhoto);
            }
          }

          // 🔥 LOAD + NORMALIZE PROFILE INTO APP STATE
          const profileSnap = await getDoc(profileRef);

          if (profileSnap.exists()) {
            const profileData = profileSnap.data();

            setUserProfile({
              ...profileData,

              // 🔒 normalize keys (prevents Name vs name bugs)
              name: profileData.Name || profileData.name || "",
              Email: user.email || profileData.Email || "",
            });
          }
        } else {
          await auth.signOut();
          setIsAuthenticated(false);
          setUserProfile(null);
          setSection("login");
        }
      } else {
        setIsAuthenticated(false);
        setUserProfile(null);
      }

      setAuthChecked(true);
    });

    return () => unsub();
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !auth.currentUser) return;

    const loadSettings = async () => {
      try {
        const uid = auth.currentUser.uid;
        const ref = doc(db, "users", uid, "settings", "main");
        const snap = await getDocs(
          query(collection(db, "users", uid, "settings"), limit(1))
        );

        if (!snap.empty) {
          const data = snap.docs[0].data();

          setSettings((prev) => ({
            ...prev,
            ...data,
          }));
        }
      } catch (err) {
        console.error("Failed to load settings:", err);
      }
    };

    loadSettings();
  }, [isAuthenticated]);

  useEffect(() => {
    const done = localStorage.getItem("onboardingDone");

    // 🔒 If user is already authenticated → NEVER show onboarding
    if (auth.currentUser) return;

    if (!done) {
      setShowOnboarding(true);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !auth.currentUser) return;

    const loadHistory = async () => {
      try {
        const uid = auth.currentUser.uid;

        const q = query(
          collection(db, "users", uid, "tests"),
          orderBy("createdAt", "desc"),
          limit(50) // 🔒 SAFE LIMIT
        );

        const snap = await getDocs(q);

        const data = snap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setTestHistory(data);
      } catch (err) {
        console.error("Failed to load history:", err);
      }
    };

    loadHistory();
  }, [isAuthenticated]);

  useEffect(() => {
    if (
      !isAuthenticated &&
      ["history", "analysis", "settings"].includes(section)
    ) {
      setSection("login");
    }
  }, [section, isAuthenticated]);

  const defaultSettings = {
    theme: "light",
    direction: "left to right",
    showNumber: true,
    showTimer: true,
    timerMode: "problem",
    showAbort: true,
    showKeypad: false,

    // 🔹 STREAM SPEED DEFAULTS
    streamSpeed: {
      easySec: 2, // default 2 seconds
      c: 2, // default rule
    },
  };

  const [settings, setSettings] = useState(defaultSettings);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  useEffect(() => {
    try {
      const saved = localStorage.getItem("settings");
      if (saved) {
        const parsed = JSON.parse(saved);
        setSettings({
          ...defaultSettings,
          ...parsed,
          streamSpeed: {
            ...defaultSettings.streamSpeed,
            ...(parsed.streamSpeed || {}),
          },
        });
      }
    } catch (e) {
      console.warn("Invalid settings in localStorage");
    }

    // 🔴 CANCEL RUNNING TEST ON REFRESH
    const wasRunning = localStorage.getItem("testInProgress");

    if (wasRunning) {
      localStorage.removeItem("testInProgress");
      // ❌ do NOT save anything
    }

    setSettingsLoaded(true);
  }, []);

  // 🔹 FIX-1: Load test history on app start

  const resetAll = () => {
    setSettings(defaultSettings);
    setSection("profile");
  };

  /* ---------- THEME APPLY + SYNC ---------- */
  useEffect(() => {
    const dark =
      settings.theme === "dark" ||
      (settings.theme === "system" &&
        window.matchMedia("(prefers-color-scheme: dark)").matches);

    const activeSidebarTheme = dark ? SIDEBAR_THEME.dark : SIDEBAR_THEME.light;

    const themeName = dark ? "dark" : "light";

    setSidebarTheme(activeSidebarTheme);

    document.body.style.background = activeSidebarTheme.bgGradient;
    document.body.style.color = activeSidebarTheme.text;
    const iframe = calcIframeRef.current;

    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.postMessage(
        {
          type: "SETTINGS_UPDATE",
          theme: themeName,
          showTimer: settings.showTimer,
          showKeypad: settings.showKeypad,
          showAbort: settings.showAbort,
          direction: settings.direction,
          timerMode: settings.timerMode,
          showNumber: settings.showNumber,
          streamSpeed: settings.streamSpeed,
        },
        "*"
      );
    }

    if (!settingsLoaded) return;
    localStorage.setItem("settings", JSON.stringify(settings));
  }, [settings, settingsLoaded]);

  useEffect(() => {
    if (!isAuthenticated || !auth.currentUser) return;

    const saveSettings = async () => {
      try {
        const uid = auth.currentUser.uid;

        await setDoc(
          doc(db, "users", uid, "settings", "main"),
          {
            ...settings,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      } catch (err) {
        console.error("Failed to save settings:", err);
      }
    };

    saveSettings();
  }, [settings, isAuthenticated]);

  /* ---------- RECEIVE TEST RESULT FROM CALCULATOR ---------- */
  useEffect(() => {
    const handler = async (e) => {
      if (!e.data || e.data.type !== "TEST_RESULT") return;

      localStorage.removeItem("testInProgress");

      // 🔒 GUEST USERS → DO NOTHING
      if (!auth.currentUser) return;

      const uid = auth.currentUser.uid;
      const payload = e.data.payload;

      try {
        // 1️⃣ Save new test
        const docRef = await addDoc(collection(db, "users", uid, "tests"), {
          ...payload,
          createdAt: serverTimestamp(),
        });

        // 2️⃣ Load tests AFTER insert (max 51)
        const q = query(
          collection(db, "users", uid, "tests"),
          orderBy("createdAt", "desc"),
          limit(51)
        );
        ff;

        const snap = await getDocs(q);

        // 3️⃣ If more than 50 → delete oldest
        if (snap.size > 50) {
          const oldestDoc = snap.docs[snap.docs.length - 1];
          await deleteDoc(doc(db, "users", uid, "tests", oldestDoc.id));
        }

        // 4️⃣ Update UI (newest first)
        setTestHistory((prev) => [{ id: docRef.id, ...payload }, ...prev]);
      } catch (err) {
        console.error("Firestore save failed:", err);
      }
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  if (!authChecked) {
    return null; // prevents auth flicker & race conditions
  }

  return (
    <>
      {showOnboarding && (
        <Onboarding
          onDone={() => {
            localStorage.setItem("onboardingDone", "true");

            localStorage.setItem("isFirstVisit", "true");

            setForceLightAuth(true);
            setSection("register"); // 🔥 GO TO CREATE ACCOUNT

            setShowOnboarding(false);
          }}
        />
      )}

      <style>{sidebarScrollbarCSS}</style>

      {/* TOP BAR */}
      {!AUTH_ONLY_SECTIONS.includes(section) && (
        <TopBar
          sidebarTheme={sidebarTheme}
          onMenuClick={() => setOpen(true)}
          userProfile={userProfile}
        />
      )}

      {!AUTH_ONLY_SECTIONS.includes(section) && (
        <Sidebar
          open={open}
          setOpen={setOpen}
          section={section}
          setSection={setSection}
          hoveredMenu={hoveredMenu}
          setHoveredMenu={setHoveredMenu}
          sidebarTheme={sidebarTheme}
          SIDEBAR_THEME={SIDEBAR_THEME}
          styles={styles}
          isAuthenticated={isAuthenticated}
          userProfile={userProfile}
        />
      )}

      {/* CONTENT */}
      <div
        style={{
          ...styles(sidebarTheme).page,

          /* 🔒 HIDE CALCULATOR WHEN AUTH / ONBOARDING IS OPEN */
          display: showOnboarding ? "none" : "block",

          visibility: open ? "hidden" : "visible",
        }}
      >
        {section === "practice" && (
          <Practice settings={settings} calcIframeRef={calcIframeRef} />
        )}

        {section === "profile" && (
          <Profile
            sidebarTheme={sidebarTheme}
            styles={styles}
            isAuthenticated={isAuthenticated}
            userProfile={userProfile} // 🔥 PASS DATA
          />
        )}

        {section === "history" && isAuthenticated && (
          <History history={testHistory} sidebarTheme={sidebarTheme} />
        )}

        {section === "analysis" && isAuthenticated && (
          <Analysis history={testHistory} sidebarTheme={sidebarTheme} />
        )}

        {section === "guides" && <Guides />}

        {section === "privacy" && (
          <PrivacyPolicy sidebarTheme={sidebarTheme} styles={styles} />
        )}

        {section === "login" && (
          <Login
            setSection={setSection}
            sidebarTheme={forceLightAuth ? SIDEBAR_THEME.light : sidebarTheme}
            clearForcedTheme={() => setForceLightAuth(false)}
          />
        )}

        {section === "register" && (
          <Register
            setSection={setSection}
            sidebarTheme={forceLightAuth ? SIDEBAR_THEME.light : sidebarTheme}
            clearForcedTheme={() => setForceLightAuth(false)}
          />
        )}

        {section === "terms-auth" && (
          <TermsConditions
            sidebarTheme={SIDEBAR_THEME.light}
            styles={styles}
            showBack
            onBack={() => setSection("login")}
          />
        )}

        {section === "terms" && (
          <TermsConditions sidebarTheme={sidebarTheme} styles={styles} />
        )}
        {section === "refund" && (
          <RefundPolicy sidebarTheme={sidebarTheme} styles={styles} />
        )}

        {section === "contact" && <Contact />}

        {section === "settings" && isAuthenticated && (
          <Settings
            settings={settings}
            setSettings={setSettings}
            sidebarTheme={sidebarTheme}
          />
        )}

        {section === "reset" && (
          <Reset
            resetAll={resetAll}
            testHistory={testHistory}
            setTestHistory={setTestHistory}
            sidebarTheme={sidebarTheme}
            exportFilteredHistory={exportFilteredHistory}
            isAuthenticated={isAuthenticated}
          />
        )}
      </div>

      <Analytics />
    </>
  );
}

const sectionPill = (theme) => ({
  display: "inline-block",
  padding: "8px 18px",
  borderRadius: 999,
  background:
    theme === SIDEBAR_THEME.dark
      ? "rgba(148,163,184,0.12)"
      : "rgba(15,23,42,0.08)",
  border: `1px solid ${theme.border}`,
  fontSize: 18,
  fontWeight: 600,
  letterSpacing: "0.3px",
});

// 🔹 History table styles
const historyTable = {
  table: {
    width: "100%",
    borderCollapse: "separate", // 🔥 IMPORTANT
    borderSpacing: "0 10px", // 🔥 ROW GAP
    marginTop: 20,
    color: "inherit",
    textAlign: "center",
  },

  thead: {
    background: "transparent",
  },

  th: {
    padding: "14px 12px",
    fontWeight: "600",
    fontSize: 14,
    color: "#cbd5f5",
  },

  tr: {
    background: "transparent",
    borderRadius: 12,
  },

  td: {
    padding: "16px 12px", // 🔥 MORE ROW HEIGHT
    fontSize: 13,
    borderTop: "1px solid transparent",
  },

  rowHover: {
    transition: "0.25s ease",
    cursor: "pointer",
  },
};

/* ================= RESET ================= */

const styles = (theme) => ({
  topBar: {
    padding: 14,
    display: "flex",
    gap: 10,
  },
  menuBtn: {
    background: "none",
    color: theme.text,
    fontSize: 18,
    border: "none",
  },

  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,.6)",
    pointerEvents: "auto",
  },

  sidebar: {
    width: "clamp(260px, 28vw, 360px)",
    height: "100%",
    padding: 26,
    background: theme.bgMain,
    color: theme.text,
    borderRight: `1px solid ${theme.border}`,
    boxShadow: "0 10px 30px rgba(0,0,0,0.15)",

    display: "flex",
    flexDirection: "column",
    overflow: "visible",
  },

  menuItem: {
    display: "flex",

    /* 🔥 CENTER TEXT PERFECTLY */
    alignItems: "center", // vertical center
    justifyContent: "center", // horizontal center
    textAlign: "center",

    width: "100%",
    minHeight: "clamp(48px, 6vh, 56px)",
    fontSize: "clamp(14px, 1.1vw, 16px)",
    padding: "14px 12px",
    fontWeight: 600,

    borderRadius: 14,
    background: "transparent",
    border: `1px solid ${theme.border}`,

    marginBottom: 12,
    cursor: "pointer",
    transition: "background 0.2s ease, box-shadow 0.2s ease",
  },

  page: { padding: "clamp(12px, 2vw, 20px)" },
  card: {
    padding: 12,
    borderRadius: 10,
    background: theme.card,
    color: theme.text,
    border: `1px solid ${theme.border}`,
    marginBottom: 10,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  pen: { fontSize: 12, cursor: "pointer" },
  abort: {
    padding: 10,
    background: "#ef4444",
    border: "none",
    color: "#fff",
  },
  table: {
    minWidth: 1500,
  },
  head: {
    display: "grid",
    gridTemplateColumns: "repeat(11, 140px)",
    fontWeight: "bold",
  },
  row: {
    display: "grid",
    gridTemplateColumns: "repeat(11, 140px)",
    borderTop: "1px solid #334155",
  },
  graph: {
    background: theme.card,
    padding: 16,
    borderRadius: 14,
    margin: "0 auto 22px",
    maxWidth: 900,
    border: `1px solid ${theme.border}`,
    color: theme.text,
  },

  graphBox: {
    height: 260,
    background: "linear-gradient(135deg,#2563eb,#22d3ee)",
    borderRadius: 10,
    marginTop: 10,
  },
  resetDefault: {
    marginTop: 10,
    padding: 10,
  },

  /* 🔹 NEW SETTINGS STYLES */
  settingRow: {
    padding: "14px 16px",
    borderRadius: 14,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
    background: theme.card,
    border: `1px solid ${theme.border}`,
  },

  settingSub: {
    fontSize: 12,
    opacity: 0.6,
  },
  profileInput: {
    background: theme.bgMain,
    color: theme.text,
    border: `1px solid ${theme.border}`,
    borderRadius: 8,
    padding: "6px 8px",
    outline: "none",
  },
});
const filterStyle = (theme) => ({
  background: theme.card,
  color: theme.text,
  border: `1px solid ${theme.border}`,
  borderRadius: 10,
  padding: "6px 10px",
  fontSize: 13,
  outline: "none",
});
